import { memo, useRef, useState } from "react";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import { DisaggregatedMetric } from "../../services/types";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getMetricGroupChartId } from "../../utils/dashboardUtils";
import { formatValue } from "../../utils/helpers";
import { Chart } from "chart.js";
import { noDataPlugin, processData } from "../../utils/chartUtils";

interface UnlabelledComparisonChartProps {
    indicators: DisaggregatedMetric[] | null;
    title: string;
    tab: string;
    chartName: string;
    height: string;
    barColor: string;
}

const UnlabelledComparisonChart = memo(({ indicators, title, tab, chartName, height, barColor }: UnlabelledComparisonChartProps) => {
    const [data, setData] = useState<{ label: string, value: number, rawValue: number | string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [barColors, setBarColors] = useState<string[]>([]);
    const backgroundColor = '#125687';

    useDeepCompareEffect(() => {
        if (!indicators) return;
        setLoading(true);

        // Find the indicator data based on the metric group chart ID
        const indicatorData = indicators.find(data => data.id === getMetricGroupChartId(tab, chartName));

        if (!indicatorData) {
            setLoading(false)
            return;
        }
        // Process chart data for labels and values
        const indicatorValues = processData(indicatorData);
    
        // Initialize the allValues array from indicator values
        let allValues = [
            ...indicatorValues
        ];

        // set barColors
        const barColors = allValues.map(_ =>barColor);

        // Update the state with the processed values and bar colors
        setData(allValues);
        setBarColors(barColors);
        setLoading(false);
    }, [indicators]);


    // Set up chart ref and instance
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"bar"> | null>(null);

    useDeepCompareEffect(() => {
        const ctx = chartRef.current?.getContext("2d");
        if (ctx) {
            // Register the plugin to all charts:
            Chart.register(ChartDataLabels, noDataPlugin);

            // Destroy existing chart instance if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Extract labels and data
            const labels = data.map(d => d.label);
            // const chartData = data.map(d => d.value);
            const rawValues = data.map(d => Number(d.rawValue));

            // Use provided barColors or fallback to backgroundColor for all bars
            const colors = barColors && barColors.length === data.length
                ? barColors
                : new Array(data.length).fill(backgroundColor);

            // State to track labels and showing no data message plugin
            const hasData = data.length > 0 && !loading;

            chartInstance.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: hasData ? labels : [],
                    datasets: hasData ? [
                        {
                            label: title,
                            data: rawValues,
                            backgroundColor: colors,
                        },
                    ] : [],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,  // Allows the chart to take the full height
                    indexAxis: 'y',
                    plugins: {
                        legend: {
                            display: false,
                        },
                        datalabels: {
                            display: false,
                        },
                        tooltip: {
                            enabled: hasData,
                            callbacks: {
                                label: (context) => {
                                    const rawValue = data[context.dataIndex].rawValue;
                                    return formatValue(rawValue, 'integer')
                                }
                            },
                            bodyColor: '#FFFFFF'
                        },
                    },
                    layout: {
                        padding: {
                            left: 10,
                            right: hasData ? 40 : 20,
                            top: 10,
                            bottom: 10,
                        }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            ticks: {
                                display: false,
                            },
                            grid: {
                                display: false,
                            },
                            border: {
                                display: false,
                                dash: [6, 4],
                            },
                            min: 0,
                            max: 200
                        },
                        y: {
                            grid: {
                                display: false,
                                drawTicks: false,
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                minRotation: 0,  // Ensure labels are parallel to y-axis
                                maxRotation: 0,  // Ensure labels are parallel to y-axis
                                font: {
                                    family: 'Gotham, sans-serif',
                                    size: 12,
                                    weight: 'bold',
                                },
                                padding: 15,
                                color: '#4D4D4D',
                                autoSkip: false,  // Ensure all labels are displayed
                                callback: function (value: any, index: number) { 
                                // Callback to wrap long text labels
                                    const maxLabelWidth = 145;
                                    const labels = this.chart.data.labels;
                                    if (!labels) {
                                        console.log(value);
                                        return;
                                    }
                                    const label = labels[index] as string;

                                    // Wrap long labels into multiple lines by words
                                    const lines = [];
                                    let currentLine = '';
                                    const words = label ? label.split(/\s+/) : [];

                                    for (let word of words) {
                                        const testLine = currentLine + (currentLine ? ' ' : '') + word;
                                        // Measure the width of the test line
                                        const testWidth = this.chart.ctx.measureText(testLine).width;

                                        if (testWidth > maxLabelWidth) {
                                            if (currentLine) {
                                                lines.push(currentLine);
                                            }
                                            currentLine = word;
                                        } else {
                                            currentLine = testLine;
                                        }
                                    }
                                    lines.push(currentLine);

                                    return lines;
                                }
                            },
                        },
                    },
                },
                plugins: [noDataPlugin],
            });
        }
    }, [data, title, backgroundColor]);

    return (
        <div className="w-full">
            <div className={height ? height : `h-80`}> {/* Adjust height as needed */}
                <canvas ref={chartRef} className="w-full bg-[#F0F0F0]"/>
            </div>
        </div>
    );
});

export default UnlabelledComparisonChart;